import { PROTOCOL_VERSION } from '../shared/protocol-version.js';
import type { PersistedRoomState } from '../shared/race-session-state.js';
import type { LocalRaceSetup } from './local-race-protocol.js';
import {
  saveOfflineRaceCheckpoint,
  type StoredOfflineRace,
} from './offline-race-store.js';

export const OFFLINE_CHECKPOINT_RETRY_DELAYS_MS = [1_000, 5_000, 15_000, 30_000] as const;

interface PendingOfflineCheckpoint {
  setup: LocalRaceSetup;
  checkpoint: PersistedRoomState;
  force: boolean;
}

export interface OfflineCheckpointPublisherOptions {
  intervalMs: number;
  save?: typeof saveOfflineRaceCheckpoint;
  /** Wall-clock time used only for persisted record timestamps. */
  now?: () => number;
  /** Monotonic elapsed time used for throttling and retries. */
  monotonicNow?: () => number;
  onDurable(record: StoredOfflineRace): void;
  onUnavailable?(): void;
}

/**
 * Publishes only IndexedDB-confirmed checkpoints to the continuation UI.
 * Failed writes retain the newest in-memory checkpoint and retry with bounded
 * backoff, while a reset invalidates every callback from the previous race.
 */
export class OfflineCheckpointPublisher {
  private pending: PendingOfflineCheckpoint | null = null;
  private inFlight: Promise<void> | null = null;
  private timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private timerDueAt = 0;
  private lastDurableAt: number | null = null;
  private retryAt = 0;
  private retryAttempt = 0;
  private generation = 0;
  private unavailableReported = false;
  private readonly save: typeof saveOfflineRaceCheckpoint;
  private readonly wallNow: () => number;
  private readonly monotonicNow: () => number;

  constructor(private readonly options: OfflineCheckpointPublisherOptions) {
    this.save = options.save ?? saveOfflineRaceCheckpoint;
    this.wallNow = options.now ?? Date.now;
    this.monotonicNow = options.monotonicNow ?? (() => globalThis.performance.now());
  }

  queue(
    setup: LocalRaceSetup,
    checkpoint: PersistedRoomState,
    force = false,
  ): void {
    this.pending = {
      setup,
      checkpoint,
      force: force || this.pending?.force === true,
    };
    if (this.inFlight) return;
    this.schedulePending();
  }

  reset(): void {
    this.generation += 1;
    this.pending = null;
    this.inFlight = null;
    this.lastDurableAt = null;
    this.retryAt = 0;
    this.retryAttempt = 0;
    this.unavailableReported = false;
    this.clearTimer();
  }

  private schedulePending(): void {
    const pending = this.pending;
    if (!pending || this.inFlight) return;
    const now = this.monotonicNow();
    if (this.retryAt > now) {
      this.scheduleAt(this.retryAt);
      return;
    }
    const intervalDueAt = (this.lastDurableAt ?? 0) + Math.max(0, this.options.intervalMs);
    if (pending.force || this.lastDurableAt === null || intervalDueAt <= now) {
      this.flush();
      return;
    }
    this.scheduleAt(intervalDueAt);
  }

  private scheduleAt(dueAt: number): void {
    if (this.timer !== undefined && this.timerDueAt <= dueAt) return;
    this.clearTimer();
    this.timerDueAt = dueAt;
    this.timer = globalThis.setTimeout(() => {
      this.timer = undefined;
      this.timerDueAt = 0;
      this.schedulePending();
    }, Math.max(0, dueAt - this.monotonicNow()));
  }

  private flush(): void {
    if (this.inFlight || !this.pending) return;
    this.clearTimer();
    const attempt = this.pending;
    this.pending = null;
    const generation = this.generation;
    const attemptedAt = this.monotonicNow();
    const savedAt = this.wallNow();
    const operation = Promise.resolve(this.save(
      attempt.setup,
      attempt.checkpoint,
      savedAt,
    )).then((saved) => {
      if (generation !== this.generation) return;
      if (!saved) {
        if (!this.pending) this.pending = { ...attempt, force: false };
        const retryDelay = OFFLINE_CHECKPOINT_RETRY_DELAYS_MS[
          Math.min(this.retryAttempt, OFFLINE_CHECKPOINT_RETRY_DELAYS_MS.length - 1)
        ]!;
        this.retryAttempt += 1;
        this.retryAt = this.monotonicNow() + retryDelay;
        if (!this.unavailableReported) {
          this.unavailableReported = true;
          this.options.onUnavailable?.();
        }
        return;
      }
      this.lastDurableAt = attemptedAt;
      this.retryAt = 0;
      this.retryAttempt = 0;
      this.unavailableReported = false;
      const generationNumber = Number(attempt.setup.offlineGeneration);
      this.options.onDurable({
        version: 1,
        protocolVersion: PROTOCOL_VERSION,
        generation: Number.isInteger(generationNumber) && generationNumber > 0
          ? generationNumber
          : 1,
        setup: attempt.setup,
        checkpoint: attempt.checkpoint,
        savedAt,
      });
    }).catch(() => {
      if (generation !== this.generation) return;
      if (!this.pending) this.pending = { ...attempt, force: false };
      const retryDelay = OFFLINE_CHECKPOINT_RETRY_DELAYS_MS[
        Math.min(this.retryAttempt, OFFLINE_CHECKPOINT_RETRY_DELAYS_MS.length - 1)
      ]!;
      this.retryAttempt += 1;
      this.retryAt = this.monotonicNow() + retryDelay;
      if (!this.unavailableReported) {
        this.unavailableReported = true;
        this.options.onUnavailable?.();
      }
    });
    const tracked = operation.finally(() => {
      if (this.inFlight === tracked) this.inFlight = null;
      if (generation === this.generation) this.schedulePending();
    });
    this.inFlight = tracked;
  }

  private clearTimer(): void {
    if (this.timer !== undefined) globalThis.clearTimeout(this.timer);
    this.timer = undefined;
    this.timerDueAt = 0;
  }
}
